import React from "react";
import { offlineCachePolicyApi } from "../../../lib/api/handlers/offlineCachePolicy";
import {
  DEFAULT_OFFLINE_CACHE_POLICY,
  getOfflineCachePolicy,
  setOfflineCachePolicy,
  subscribeOfflineCachePolicy,
  type OfflineCachePolicy,
} from "../../../lib/offline/cachePolicy";
import { useAuthSession } from "../../../lib/auth/session";

const OfflineCachePolicyContext = React.createContext<OfflineCachePolicy>(DEFAULT_OFFLINE_CACHE_POLICY);

export const OfflineCachePolicyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [policy, setPolicy] = React.useState<OfflineCachePolicy>(getOfflineCachePolicy());
  const { officerId } = useAuthSession();

  React.useEffect(() => {
    let cancelled = false;
    const unsubscribe = subscribeOfflineCachePolicy((next) => {
      if (!cancelled) {
        setPolicy(next);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    if (!officerId) {
      return;
    }
    let cancelled = false;
    const fetchPolicy = async () => {
      try {
        const remote = await offlineCachePolicyApi.getPolicy();
        if (!cancelled) {
          setOfflineCachePolicy(remote);
        }
      } catch (error) {
        console.warn("Failed to load offline cache policy; continuing with defaults", error);
      }
    };

    fetchPolicy().catch(() => {
      /* logged above */
    });

    return () => {
      cancelled = true;
    };
  }, [officerId]);

  return <OfflineCachePolicyContext.Provider value={policy}>{children}</OfflineCachePolicyContext.Provider>;
};

export const useOfflineCachePolicy = (): OfflineCachePolicy => React.useContext(OfflineCachePolicyContext);
